import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { serverTimestamp, Timestamp } from 'firebase/firestore';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { BankingPartner } from '../types';
import { bankingPartnerService } from '../services/bankingPartnerService';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';

const formSchema = z.object({
  name: z.string().min(2, {
    message: 'Partner name must be at least 2 characters.',
  }),
  status: z.enum(['active', 'inactive', 'pending'], {
    required_error: 'Please select a status.',
  }),
  color: z.string().optional(),
  description: z.string().optional(),
  website: z.string().url().optional().or(z.literal('')),
  apiKey: z.string().optional(),
  apiBaseUrl: z.string().url().optional().or(z.literal('')),
});

interface BankingPartnerFormProps {
  initialData?: BankingPartner;
  onSuccess?: (partner: BankingPartner) => void;
}

export function BankingPartnerForm({ initialData, onSuccess }: BankingPartnerFormProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: initialData?.name || '',
      status: initialData?.status || 'pending',
      color: initialData?.color || '#000000',
      description: initialData?.description || '',
      website: initialData?.website || '',
      apiKey: initialData?.apiKey || '',
      apiBaseUrl: initialData?.apiBaseUrl || '',
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      if (initialData) {
        await bankingPartnerService.updateBankingPartner(initialData.id, values);
        toast({
          title: 'Success',
          description: 'Banking partner updated successfully',
        });
      } else {
        const id = await bankingPartnerService.createBankingPartner({
          name: values.name,
          status: values.status,
          color: values.color,
          description: values.description,
          website: values.website,
          apiKey: values.apiKey,
          apiBaseUrl: values.apiBaseUrl,
          contacts: [],
          agreements: [],
        });
        toast({
          title: 'Success',
          description: 'Banking partner created successfully',
        });
        navigate(`/app/banking-partners/${id}`);
      }

      if (onSuccess) {
        onSuccess({
          id: initialData?.id || '',
          name: values.name,
          status: values.status,
          color: values.color,
          description: values.description,
          website: values.website,
          apiKey: values.apiKey,
          apiBaseUrl: values.apiBaseUrl,
          contacts: [],
          agreements: [],
          createdAt: serverTimestamp() as Timestamp,
          updatedAt: serverTimestamp() as Timestamp,
        });
      }
    } catch (error) {
      console.error('Error saving banking partner:', error);
      toast({
        title: 'Error',
        description: 'Failed to save banking partner',
        variant: 'destructive',
      });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Partner Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter partner name" {...field} />
              </FormControl>
              <FormDescription>
                The name of the banking partner organization.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Status</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
              <FormDescription>
                The current status of the partnership.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="color"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Color</FormLabel>
              <FormControl>
                <div className="flex gap-2 items-center">
                  <Input type="color" {...field} className="w-16 h-10" />
                  <Input {...field} placeholder="#000000" className="flex-1" />
                </div>
              </FormControl>
              <FormDescription>
                Color used to identify this banking partner.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Input placeholder="Brief description of the partner" {...field} />
              </FormControl>
              <FormDescription>
                Optional description of the banking partner.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="website"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Website</FormLabel>
              <FormControl>
                <Input placeholder="https://example.com" {...field} />
              </FormControl>
              <FormDescription>
                Partner's website URL.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="apiKey"
          render={({ field }) => (
            <FormItem>
              <FormLabel>API Key</FormLabel>
              <FormControl>
                <Input type="password" placeholder="Enter PayArc API Key" {...field} />
              </FormControl>
              <FormDescription>
                PayArc API Key for this banking partner (optional, only required for PayArc partners).
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="apiBaseUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>API Base URL</FormLabel>
              <FormControl>
                <Input placeholder="https://api.payarc.net" {...field} />
              </FormControl>
              <FormDescription>
                PayArc API Base URL. Use https://testapi.payarc.net for sandbox or https://api.payarc.net for production.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/app/banking-partners')}
          >
            Cancel
          </Button>
          <Button type="submit">
            {initialData ? 'Update Partner' : 'Create Partner'}
          </Button>
        </div>
      </form>
    </Form>
  );
}